/**
 * Frontend JavaScript for Magnet Calculator Block
 * Handles interactivity for the calculator on the public-facing site
 */

import { openParadotModal } from './utils/paradot-form';

// Calculator constants (same as in calculator.js)
const INVESTIGATOR_SAVINGS_PERCENTAGE = 0.30;
const EXAMINER_SAVINGS_PERCENTAGE = 0.55;
const INVESTIGATOR_HOURLY_COST = 46;
const EXAMINER_HOURLY_COST = 46;

/**
 * Calculate hours saved based on input parameters
 */
function calculateHoursSaved(casesPerYear, processingHoursPerCase, analysisHoursPerCase) {
    const investigatorTotalHours = casesPerYear * processingHoursPerCase;
    
    // H53: =F6*F8*I15 - Analysis hours saved
    // const investigatorSavedHours = investigatorTotalHours * INVESTIGATOR_SAVINGS_PERCENTAGE;
    const investigatorSavedHours = investigatorTotalHours * EXAMINER_SAVINGS_PERCENTAGE;

    const examinerTotalHours = casesPerYear * analysisHoursPerCase;
    // H53: =F6*F8*I15 - Analysis hours saved
    // const examinerSavedHours = examinerTotalHours * EXAMINER_SAVINGS_PERCENTAGE;
    const examinerSavedHours = examinerTotalHours * INVESTIGATOR_SAVINGS_PERCENTAGE;

    return Math.round(investigatorSavedHours + examinerSavedHours);
}

/**
 * Calculate value created (money saved) based on input parameters
 */
function calculateValueCreated(casesPerYear, processingHoursPerCase, analysisHoursPerCase) {
    const investigatorTotalHours = casesPerYear * processingHoursPerCase;

    // H53: =F6*F8*I15 - Analysis hours saved
    // const investigatorSavedHours = investigatorTotalHours * INVESTIGATOR_SAVINGS_PERCENTAGE;
    const investigatorSavedHours = investigatorTotalHours * EXAMINER_SAVINGS_PERCENTAGE;
    const investigatorMoneySaved = investigatorSavedHours * INVESTIGATOR_HOURLY_COST;

    const examinerTotalHours = casesPerYear * analysisHoursPerCase;
    // H53: =F6*F8*I15 - Analysis hours saved
    // const examinerSavedHours = examinerTotalHours * EXAMINER_SAVINGS_PERCENTAGE;
    const examinerSavedHours = examinerTotalHours * INVESTIGATOR_SAVINGS_PERCENTAGE;
    const examinerMoneySaved = examinerSavedHours * EXAMINER_HOURLY_COST;

    return Math.round(investigatorMoneySaved + examinerMoneySaved);
}

/**
 * Format number with commas for display
 */
function formatNumber(num) {
    return num.toLocaleString();
}

/**
 * Format currency for display
 */
function formatCurrency(amount) {
    return `$${amount.toLocaleString()}`;
}

/**
 * Get URL parameter value
 * @param {string} paramName - Name of the URL parameter to get
 * @returns {string|null} - Parameter value or null if not found
 */
function getURLParameter(paramName) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(paramName);
}

/**
 * Handle calculator form submission
 * Opens Paradot form in a modal and handles the complete flow
 */
function handleCalculatorSubmit(event) {
    event.preventDefault();

    const calculatorElement = event.target.closest('.magnet-calculator');
    if (!calculatorElement) return;

    // Get current calculation values
    const casesSlider = calculatorElement.querySelector('input[data-slider="cases"]');
    const processingSlider = calculatorElement.querySelector('input[data-slider="processing"]');
    const analysisSlider = calculatorElement.querySelector('input[data-slider="analysis"]');

    if (!casesSlider || !processingSlider || !analysisSlider) return;

    const cases = parseInt(casesSlider.value);
    const processing = parseInt(processingSlider.value);
    const analysis = parseInt(analysisSlider.value);

    // Calculate current results
    const hoursSaved = calculateHoursSaved(cases, processing, analysis);
    const valueCreated = calculateValueCreated(cases, processing, analysis);

    // Get UTM parameters - prioritize URL query string over data attributes
    const utmSource = getURLParameter('utm_source') || getCookie('utm_source') || calculatorElement.dataset.utmSource || '';
    const utmMedium = getURLParameter('utm_medium') || getCookie('utm_medium') || calculatorElement.dataset.utmMedium || '';
    const utmCampaign = getURLParameter('utm_campaign') || getCookie('utm_campaign') || calculatorElement.dataset.utmCampaign || '';
    const utmTerm = getURLParameter('utm_term') || getCookie('utm_term') || calculatorElement.dataset.utmTerm || '';
    const utmContent = getURLParameter('utm_content') || getCookie('utm_content') || calculatorElement.dataset.utmContent || '';
    const utmAudience = getURLParameter('utm_audience') || getCookie('utm_audience') || calculatorElement.dataset.utmAudience || '';

    // Get URLs from data attributes (now configurable in block settings)
    const paradotFormUrl = calculatorElement.dataset.paradotFormUrl || 'https://go.magnetforensics.com/l/52162/2025-08-20/llzfwb';
    const redirectUrl = calculatorElement.dataset.redirectUrl || 'http://localhost/gutenburg/index.php/sample-page-2/';

    // Prepare data for Paradot
    const paradotData = {
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_campaign: utmCampaign,
        utm_term: utmTerm,
        utm_content: utmContent,
        utm_audience: utmAudience,
        roi_cases_per_year: cases,
        roi_processing_hours: processing,
        roi_analysis_hours: analysis,
        roi_hours_saved: hoursSaved,
        roi_value_created: valueCreated,
        roi_calc_results_links: `${redirectUrl}?utm_source=${encodeURIComponent(utmSource)}&utm_medium=${encodeURIComponent(utmMedium)}&utm_campaign=${encodeURIComponent(utmCampaign)}&utm_term=${encodeURIComponent(utmTerm)}&utm_content=${encodeURIComponent(utmContent)}&utm_audience=${encodeURIComponent(utmAudience)}&cases_per_year=${cases}&processing_hours=${processing}&analysis_hours=${analysis}&hours_saved=${hoursSaved}&value_created=${valueCreated}`
    };

    // Open Paradot form in a modal
    openParadotModal(paradotFormUrl, paradotData, redirectUrl, getResultsPageURL, redirectToResultsPage);
}
/**
 * Redirect to results page with all parameters
 */
function getResultsPageURL(redirectUrl, data) {
    const params = new URLSearchParams({
        // utm_source: data.utm_source,
        // utm_medium: data.utm_medium,
        // utm_campaign: data.utm_campaign,
        // utm_term: data.utm_term,
        // utm_content: data.utm_content,
        // utm_audience: data.utm_audience,
        cases_per_year: data.roi_cases_per_year,
        processing_hours: data.roi_processing_hours,
        analysis_hours: data.roi_analysis_hours,
        hours_saved: data.roi_hours_saved,
        value_created: data.roi_value_created,
        // roi_calc_results_links: data.roi_calc_results_links
    });

    const finalUrl = `${redirectUrl}?${params.toString()}`;
    return finalUrl;
}
/**
 * Redirect to results page with all parameters
 */
function redirectToResultsPage(redirectUrl, data) {
    const params = new URLSearchParams({
        // utm_source: data.utm_source,
        // utm_medium: data.utm_medium,
        // utm_campaign: data.utm_campaign,
        // utm_term: data.utm_term,
        // utm_content: data.utm_content,
        // utm_audience: data.utm_audience,
        cases_per_year: data.roi_cases_per_year,
        processing_hours: data.roi_processing_hours,
        analysis_hours: data.roi_analysis_hours,
        hours_saved: data.roi_hours_saved,
        value_created: data.roi_value_created,
        // roi_calc_results_links: data.roi_calc_results_links
    });

    const finalUrl = `${redirectUrl}?${params.toString()}`;
    window.location.href = finalUrl;
}

/**
 * Initialize mobile calculator slideup functionality
 */
function initializeMobileCalculator(calculatorElement) {
    const triggerBtn = document.querySelector('.mobile-trigger-btn');
    const closeBtn = calculatorElement.querySelector('.mobile-close-btn');

    if (!triggerBtn || !closeBtn) return;

    // Show calculator slideup
    triggerBtn.addEventListener('click', () => {
        calculatorElement.classList.add('mobile-active');
        document.body.classList.add('mobile-calculator-open');
        // Small delay to allow CSS transition to work properly
        setTimeout(() => calculatorElement.classList.add('slide-up'), 50);
    });

    // Hide calculator slideup
    closeBtn.addEventListener('click', () => {
        calculatorElement.classList.remove('slide-up');
        setTimeout(() => {
            calculatorElement.classList.remove('mobile-active');
            document.body.classList.remove('mobile-calculator-open');
        }, 300); // Match CSS transition duration
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && calculatorElement.classList.contains('mobile-active')) {
            closeBtn.click();
        }
    });
}

/**
 * Initialize calculator functionality for a given calculator element
 */
function initializeCalculator(calculatorElement) {
    // Get all sliders (input elements)
    const casesSlider = calculatorElement.querySelector('input[data-slider="cases"]');
    const processingSlider = calculatorElement.querySelector('input[data-slider="processing"]');
    const analysisSlider = calculatorElement.querySelector('input[data-slider="analysis"]');

    // Get text inputs for synchronization
    const casesTextInput = calculatorElement.querySelector('input[type="number"][data-slider="cases"]');
    const processingTextInput = calculatorElement.querySelector('input[type="number"][data-slider="processing"]');
    const analysisTextInput = calculatorElement.querySelector('input[type="number"][data-slider="analysis"]');

    // Get value display elements (span elements)
    const casesValue = calculatorElement.querySelector('span[data-slider="cases"]');
    const processingValue = calculatorElement.querySelector('span[data-slider="processing"]');
    const analysisValue = calculatorElement.querySelector('span[data-slider="analysis"]');

    // Get result display elements
    const hoursResult = calculatorElement.querySelector('[data-result="hours"]');
    const valueResult = calculatorElement.querySelector('[data-result="value"]');

    if (!casesSlider || !processingSlider || !analysisSlider || !hoursResult || !valueResult) {
        console.warn('Magnet Calculator: Missing required elements', {
            casesSlider: !!casesSlider,
            processingSlider: !!processingSlider,
            analysisSlider: !!analysisSlider,
            hoursResult: !!hoursResult,
            valueResult: !!valueResult
        });
        return;
    }

    /**
     * Update calculations and display
     */
    function updateCalculations() {
        const cases = parseInt(casesSlider.value);
        const processing = parseInt(processingSlider.value);
        const analysis = parseInt(analysisSlider.value);

        // Get min/max values from the calculator element data attributes
        const minCases = parseInt(calculatorElement.dataset.minCases) || 1;
        const maxCases = parseInt(calculatorElement.dataset.maxCases) || 2000;
        const minProcessing = parseInt(calculatorElement.dataset.minProcessingHours) || 1;
        const maxProcessing = parseInt(calculatorElement.dataset.maxProcessingHours) || 50;
        const minAnalysis = parseInt(calculatorElement.dataset.minAnalysisHours) || 1;
        const maxAnalysis = parseInt(calculatorElement.dataset.maxAnalysisHours) || 30;

        // Update slider value displays
        if (casesValue) casesValue.textContent = cases;
        if (processingValue) processingValue.textContent = processing;
        if (analysisValue) analysisValue.textContent = analysis;

        // Update text inputs to match range slider values
        if (casesTextInput) casesTextInput.value = cases;
        if (processingTextInput) processingTextInput.value = processing;
        if (analysisTextInput) analysisTextInput.value = analysis;

        // Update slider visual components (fill and thumb positions)
        updateSliderVisuals(calculatorElement, 'cases', cases, minCases, maxCases);
        updateSliderVisuals(calculatorElement, 'processing', processing, minProcessing, maxProcessing);
        updateSliderVisuals(calculatorElement, 'analysis', analysis, minAnalysis, maxAnalysis);

        // Calculate results
        const hours = calculateHoursSaved(cases, processing, analysis);
        const value = calculateValueCreated(cases, processing, analysis);

        // Update result displays
        hoursResult.textContent = formatNumber(hours);
        valueResult.textContent = formatNumber(value);
    }

    /**
     * Update slider visual components (fill and thumb)
     */
    function updateSliderVisuals(container, sliderType, value, min, max) {
        const percentage = ((value - min) / (max - min)) * 100;
        const fill = container.querySelector(`.magnet-calculator__slider--${sliderType} .magnet-calculator__slider-fill`);
        const thumb = container.querySelector(`.magnet-calculator__slider--${sliderType} .magnet-calculator__slider-thumb`);

        if (fill) {
            fill.style.width = `${percentage}%`;
        }
        if (thumb) {
            thumb.style.left = `${percentage}%`;
        }
    }

    /**
     * Handle range input changes - update corresponding text input
     */
    function handleRangeInputChange(event) {
        const sliderType = event.target.getAttribute('data-slider');
        const textInput = calculatorElement.querySelector(`input[type="number"][data-slider="${sliderType}"]`);
        
        if (textInput) {
            const value = parseInt(event.target.value, 10);
            textInput.value = value;
        }
        
        updateCalculations();
    }

    /**
     * Handle text input changes - update corresponding range input
     */
    function handleTextInputChange(event) {
        const sliderType = event.target.getAttribute('data-slider');
        const rangeInput = calculatorElement.querySelector(`input[type="range"][data-slider="${sliderType}"]`);
        const textInput = calculatorElement.querySelector(`input[type="number"][data-slider="${sliderType}"]`);
        
        if (rangeInput) {
            // Remove non-numeric characters for parsing
            const numericValue = event.target.value;
            let value = parseInt(numericValue, 10);
            
            // Get min/max values to validate
            const min = parseInt(rangeInput.getAttribute('min'), 10);
            const max = parseInt(rangeInput.getAttribute('max'), 10);
            
            // If the value is valid, update both inputs
            if (!isNaN(value)) {
                if ( value < min ) {
                    value = min;
                }
                if ( value > max ) {
                    value = max;
                }
            } else if (numericValue === '') {
                value = min;
            } else {
                value = min;
            }
            event.target.value = value;
            rangeInput.value = value;
            textInput.value = value;
            updateCalculations();
        }
    }


    // Add event listeners to range sliders
    casesSlider.addEventListener('input', handleRangeInputChange);
    processingSlider.addEventListener('input', handleRangeInputChange);
    analysisSlider.addEventListener('input', handleRangeInputChange);

    // const debouncedHandleTextInputChange = debounce(handleTextInputChange, 500);
    // Add event listeners to text inputs for synchronization
    if (casesTextInput) {
        casesTextInput.addEventListener('blur', handleTextInputChange);
    }
    if (processingTextInput) {
        processingTextInput.addEventListener('blur', handleTextInputChange);
    }
    if (analysisTextInput) {
        analysisTextInput.addEventListener('blur', handleTextInputChange);
    }

    // Add event listener to the submit button
    const submitButton = calculatorElement.querySelector('.magnet-calculator__button');
    if (submitButton) {
        submitButton.addEventListener('click', handleCalculatorSubmit);
    }

    // Initial calculation to ensure values are up to date
    updateCalculations();

    // Initialize mobile calculator functionality
    // initializeMobileCalculator(calculatorElement);
}

/**
 * Get URL parameters
 */
function getURLParameters() {
    const urlParams = new URLSearchParams(window.location.search);
    return {
        cases_per_year: parseInt(urlParams.get('cases_per_year')) || 1000,
        processing_hours: parseInt(urlParams.get('processing_hours')) || 20,
        analysis_hours: parseInt(urlParams.get('analysis_hours')) || 12,
        hours_saved: parseInt(urlParams.get('hours_saved')) || null,
        value_created: parseInt(urlParams.get('value_created')) || null
    };
}

/**
 * Initialize time savings block
 * Using exact Excel formulas from magnet-calculator-logic.xlsx
 */
function initializeTimeSavingsBlock(blockElement) {
    const params = getURLParameters();

    // Input values (Excel F6, F7, F8)
    const casesPerYear = params.cases_per_year;       // F6
    const processingHours = params.processing_hours;  // F7
    const analysisHours = params.analysis_hours;      // F8

    // Excel formulas:
    // H51: =F6*(F7+F8) - Total hours before
    const totalHoursBefore = casesPerYear * (processingHours + analysisHours);

    // H52: =F6*F7*I15 - Processing hours saved
    const processingSavings = Math.round(casesPerYear * processingHours * INVESTIGATOR_SAVINGS_PERCENTAGE);

    // H53: =F6*F8*I22 - Analysis hours saved
    const analysisSavings = Math.round(casesPerYear * analysisHours * EXAMINER_SAVINGS_PERCENTAGE);

    // H54: =H52+H53 - Total hours saved
    const totalSavings = processingSavings + analysisSavings;

    // Total hours after = before - saved
    const totalHoursAfter = totalHoursBefore - totalSavings;

    // Calculate height percentages for chart bars (using totalHoursBefore as 100%)
    const processingHeightPercent = (processingSavings / totalHoursBefore) * 100;
    const analysisHeightPercent = (analysisSavings / totalHoursBefore) * 100;
    const afterHeightPercent = (totalHoursAfter / totalHoursBefore) * 100;

    // Update display elements
    const elements = {
        'total-hours-before': formatNumber(totalHoursBefore),
        'processing-savings': formatNumber(processingSavings),
        'analysis-savings': formatNumber(analysisSavings),
        'total-savings': formatNumber(totalSavings),
        'total-hours-after': formatNumber(totalHoursAfter)
    };

    // Update all elements with calculated values
    Object.keys(elements).forEach(key => {
        const spans = blockElement.querySelectorAll(`[data-calc="${key}"]`);
        spans.forEach(span => {
            span.textContent = elements[key];
        });
    });

    // Update chart bar heights
    const chartBars = {
        'chart-before': 100, // Always 100% height
        'chart-processing': processingHeightPercent,
        'chart-analysis': analysisHeightPercent,
        'chart-after-remaining': afterHeightPercent,
        'chart-after-processing': processingHeightPercent,
        'chart-after-analysis': analysisHeightPercent
    };

    // Update chart elements with height percentages
    Object.keys(chartBars).forEach(key => {
        const elements = blockElement.querySelectorAll(`[data-chart="${key}"]`);
        elements.forEach(element => {
            element.setAttribute('data-height-percent', chartBars[key]);

            if (element.tagName === 'rect') {
                const maxHeight = 156; // Base height in pixels
                const height = (chartBars[key] / 100) * maxHeight;
                element.setAttribute('height', height);

                // Update the parent SVG height to match the rect height for stacked bars
                const parentSvg = element.parentElement;
                if (key.startsWith('chart-after-')) {
                    parentSvg.setAttribute('height', height);
                }

                // For single bars, position from bottom
                if (key === 'chart-before') {
                    element.setAttribute('y', 0); // Fixed top position for full height bar
                } else if (key === 'chart-processing' || key === 'chart-analysis') {
                    const yPos = maxHeight - height;
                    element.setAttribute('y', yPos);
                }
                // For stacked bars, y=0 since flexbox handles positioning
            }
        });
    });
}

/**
 * Initialize total value block
 * Using exact Excel formulas from magnet-calculator-logic.xlsx
 */
function initializeTotalValueBlock(blockElement) {
    const params = getURLParameters();

    // Input values (Excel F6, F7, F8)
    const casesPerYear = params.cases_per_year;       // F6
    const processingHours = params.processing_hours;  // F7
    const analysisHours = params.analysis_hours;      // F8

    // Excel formulas:
    // H62: =F6*(F7+F8)*46 - Total labor cost before
    const laborCostBefore = casesPerYear * (processingHours + analysisHours) * 46;

    // Calculate hours saved first (from time savings calculations)
    // H52: =F6*F7*I15 - Processing hours saved
    const processingSavedHours = casesPerYear * processingHours * INVESTIGATOR_SAVINGS_PERCENTAGE;

    // H53: =F6*F8*I22 - Analysis hours saved
    const analysisSavedHours = casesPerYear * analysisHours * EXAMINER_SAVINGS_PERCENTAGE;

    // Calculate money saved (from Excel H34 and H39 logic)
    // H34: =H32*H33 (Investigator hours saved * $46)
    const automateValue = Math.round(processingSavedHours * INVESTIGATOR_HOURLY_COST);

    // H39: =H37*H38 (Examiner hours saved * $46)
    const reviewValue = Math.round(analysisSavedHours * EXAMINER_HOURLY_COST);

    // H43: =H39+H34 - Total value created
    const totalValueCreated = automateValue + reviewValue;

    // H64: =H62-H63 - Total labor cost after (before - savings)
    const laborCostAfter = laborCostBefore - totalValueCreated;

    // Update display elements
    const elements = {
        'labor-cost-before': formatNumber(laborCostBefore),
        'automate-value': formatNumber(automateValue),
        'review-value': formatNumber(reviewValue),
        'total-value-created': formatNumber(totalValueCreated),
        'labor-cost-after': formatNumber(laborCostAfter)
    };

    // Update all elements with calculated values
    Object.keys(elements).forEach(key => {
        const spans = blockElement.querySelectorAll(`[data-calc="${key}"]`);
        spans.forEach(span => {
            span.textContent = elements[key];
        });
    });

    // Calculate height percentages for value chart (using laborCostBefore as 100%)
    const valueCreatedHeightPercent = (totalValueCreated / laborCostBefore) * 100;
    const afterHeightPercent = (laborCostAfter / laborCostBefore) * 100;

    // Update chart bar heights for value chart
    const valueChartBars = {
        'value-chart-before': 100, // Always 100% height
        'value-chart-created': valueCreatedHeightPercent,
        'value-chart-after-remaining': afterHeightPercent,
        'value-chart-after-created': valueCreatedHeightPercent
    };

    // Update value chart elements with height percentages
    Object.keys(valueChartBars).forEach(key => {
        const elements = blockElement.querySelectorAll(`[data-chart="${key}"]`);
        elements.forEach(element => {
            element.setAttribute('data-height-percent', valueChartBars[key]);

            if (element.tagName === 'rect') {
                const maxHeight = 156; // Base height in pixels
                const height = (valueChartBars[key] / 100) * maxHeight;
                element.setAttribute('height', height);

                // Update the parent SVG height to match the rect height for stacked bars
                const parentSvg = element.parentElement;
                if (key.startsWith('value-chart-after-')) {
                    parentSvg.setAttribute('height', height);
                }

                // For single bars, position from bottom
                if (key === 'value-chart-before') {
                    element.setAttribute('y', 0); // Fixed top position for full height bar
                } else if (key === 'value-chart-created') {
                    const yPos = maxHeight - height;
                    element.setAttribute('y', yPos);
                }
                // For stacked bars, y=0 since flexbox handles positioning
            }
        });
    });
}

/**
 * Initialize time savings block
 * Using exact Excel formulas from magnet-calculator-logic.xlsx
 */
function initializeTimeSavingsBlockNew(blockElement) {
    const params = getURLParameters();

    // Input values (Excel F6, F7, F8)
    const casesPerYear = params.cases_per_year;       // F6
    const processingHours = params.processing_hours;  // F7
    const analysisHours = params.analysis_hours;      // F8

    newCalculateTime( casesPerYear, processingHours, analysisHours, blockElement );

    let casesInput = blockElement.querySelector('input[data-calc="forensics-cases"]');
    let processingInput = blockElement.querySelector('input[data-calc="processing-hours"]');
    let analysisInput = blockElement.querySelector('input[data-calc="analysis-hours"]');

    casesInput.value = casesPerYear;
    processingInput.value = processingHours;
    analysisInput.value = analysisHours;

    const handleInputChange = (event) => {
        const { id, value } = event.target;
        let casesPerYear_ = parseInt(casesInput.value);
        if ( !casesPerYear_ || casesPerYear_ < casesInput.min ) {
            casesPerYear_ = casesInput.min;
        }
        if ( casesPerYear_ > casesInput.max ) {
            casesPerYear_ = casesInput.max;
        }
        let processingHours_ = parseInt(processingInput.value);
        if ( !processingHours_ || processingHours_ < processingInput.min ) {
            processingHours_ = processingInput.min;
        }
        if ( processingHours_ > processingInput.max ) {
            processingHours_ = processingInput.max;
        }
        let analysisHours_ = parseInt(analysisInput.value);
        if ( !analysisHours_ || analysisHours_ < analysisInput.min ) {
            analysisHours_ = analysisInput.min;
        }
        if ( analysisHours_ > analysisInput.max ) {
            analysisHours_ = analysisInput.max;
        }

        // casesInput.value = casesPerYear_;
        let casesInputs = document.querySelectorAll('input[data-calc="forensics-cases"]');
        casesInputs.forEach(input => {
            input.value = casesPerYear_;
            if ( input.id !== casesInput.id ) {
                newCalculateValue(casesPerYear_, processingHours_, analysisHours_, document);
            }
        });
        // processingInput.value = processingHours_;
        let processingInputs = document.querySelectorAll('input[data-calc="processing-hours"]');
        processingInputs.forEach(input => {
            input.value = processingHours_;
            if ( input.id !== processingInput.id ) {
                newCalculateValue(casesPerYear_, processingHours_, analysisHours_, document);
            }
        });
        // analysisInput.value = analysisHours_;
        let analysisInputs = document.querySelectorAll('input[data-calc="analysis-hours"]');
        analysisInputs.forEach(input => {
            input.value = analysisHours_;
            if ( input.id !== analysisInput.id ) {
                newCalculateValue(casesPerYear_, processingHours_, analysisHours_, document);
            }
        });
        newCalculateTime(casesPerYear_, processingHours_, analysisHours_, blockElement);
    };

    // const debouncedHandleInputChange = debounce(handleInputChange, 500);
    casesInput.addEventListener('change', handleInputChange);
    processingInput.addEventListener('change', handleInputChange);
    analysisInput.addEventListener('change', handleInputChange);
}

/**
 * Initialize total value block (new)
 * Using exact Excel formulas from magnet-calculator-logic.xlsx
 */
function initializeTotalValueBlockNew(blockElement) {
    const params = getURLParameters();

    // Input values (Excel F6, F7, F8)
    const casesPerYear = params.cases_per_year;       // F6
    const processingHours = params.processing_hours;  // F7
    const analysisHours = params.analysis_hours;      // F8

    newCalculateValue( casesPerYear, processingHours, analysisHours, blockElement );

    let casesInput = blockElement.querySelector('input[data-calc="forensics-cases"]');
    let processingInput = blockElement.querySelector('input[data-calc="processing-hours"]');
    let analysisInput = blockElement.querySelector('input[data-calc="analysis-hours"]');

    casesInput.value = casesPerYear;
    processingInput.value = processingHours;
    analysisInput.value = analysisHours;

    const handleInputChange = (event) => {
        const { id, value } = event.target;
        let casesPerYear_ = parseInt(casesInput.value);
        if ( !casesPerYear_ || casesPerYear_ < casesInput.min ) {
            casesPerYear_ = casesInput.min;
        }
        if ( casesPerYear_ > casesInput.max ) {
            casesPerYear_ = casesInput.max;
        }

        let processingHours_ = parseInt(processingInput.value);
        if ( !processingHours_ || processingHours_ < processingInput.min ) {
            processingHours_ = processingInput.min;
        }
        if ( processingHours_ > processingInput.max ) {
            processingHours_ = processingInput.max;
        }

        let analysisHours_ = parseInt(analysisInput.value);
        if ( !analysisHours_ || analysisHours_ < analysisInput.min ) {
            analysisHours_ = analysisInput.min;
        }
        if ( analysisHours_ > analysisInput.max ) {
            analysisHours_ = analysisInput.max;
        }

        // casesInput.value = casesPerYear_;
        let casesInputs = document.querySelectorAll('input[data-calc="forensics-cases"]');
        casesInputs.forEach(input => {
            input.value = casesPerYear_;
            if ( input.id !== casesInput.id ) {
                newCalculateTime(casesPerYear_, processingHours_, analysisHours_, document);
            }
        });
        // processingInput.value = processingHours_;
        let processingInputs = document.querySelectorAll('input[data-calc="processing-hours"]');
        processingInputs.forEach(input => {
            input.value = processingHours_;
            if ( input.id !== processingInput.id ) {
                newCalculateTime(casesPerYear_, processingHours_, analysisHours_, document);
            }
        });
        // analysisInput.value = analysisHours_;
        let analysisInputs = document.querySelectorAll('input[data-calc="analysis-hours"]');
        analysisInputs.forEach(input => {
            input.value = analysisHours_;
            if ( input.id !== analysisInput.id ) {
                newCalculateTime(casesPerYear_, processingHours_, analysisHours_, document);
            }
        });
        newCalculateValue(casesPerYear_, processingHours_, analysisHours_, blockElement);
    };

    // const debouncedHandleInputChange = debounce(handleInputChange, 500);
    casesInput.addEventListener('blur', handleInputChange);
    processingInput.addEventListener('blur', handleInputChange);
    analysisInput.addEventListener('blur', handleInputChange);
}

function newCalculateTime(casesPerYear, processingHours, analysisHours, blockElement) {
    // Excel formulas:
    // H51: =F6*(F7+F8) - Total hours before
    const totalHoursBefore = casesPerYear * (processingHours + analysisHours);

    // old: H52: =F6*F7*I15 - Processing hours saved
    // const processingSavings = Math.round(casesPerYear * processingHours * INVESTIGATOR_SAVINGS_PERCENTAGE);
    // H52: =F6*F7*I22 - Processing hours saved
    const processingSavings = Math.round(casesPerYear * processingHours * EXAMINER_SAVINGS_PERCENTAGE);

    // old: H53: =F6*F8*I22 - Analysis hours saved
    // const analysisSavings = Math.round(casesPerYear * analysisHours * EXAMINER_SAVINGS_PERCENTAGE);
    // H53: =F6*F8*I15 - Analysis hours saved
    const analysisSavings = Math.round(casesPerYear * analysisHours * INVESTIGATOR_SAVINGS_PERCENTAGE);

    // H54: =H52+H53 - Total hours saved
    const totalSavings = processingSavings + analysisSavings;

    // Total hours after = before - saved
    const totalHoursAfter = totalHoursBefore - totalSavings;

    // Update display elements
    const elements = {
        'total-hours-before': formatNumber(totalHoursBefore),
        'processing-savings': formatNumber(processingSavings),
        'analysis-savings': formatNumber(analysisSavings),
        'total-savings': formatNumber(totalSavings),
        'total-hours-after': formatNumber(totalHoursAfter)
    };

    // Update all elements with calculated values
    Object.keys(elements).forEach(key => {
        const spans = blockElement.querySelectorAll(`[data-calc="${key}"]`);
        spans.forEach(span => {
            span.textContent = elements[key];
        });
    });

    // after value relative to before
    const afterWidth = (totalHoursAfter / totalHoursBefore).toFixed(2);

    // Update chart bar heights
    const chartBars = {
        'value-chart-after': afterWidth,
    };

    // Update chart elements with height percentages
    Object.keys(chartBars).forEach(key => {
        const elements = blockElement.querySelectorAll(`[data-chart="${key}"]`);
        elements.forEach(element => {
            element.setAttribute('data-width-percent', chartBars[key]);

            if (element.tagName === 'rect' && key === 'value-chart-after') {
                element.parentNode.parentNode.style.width = `calc( ( ( 100% - 120px )  * ${afterWidth} ) + 120px)`;
            }
        });
    });
}

function newCalculateValue(casesPerYear, processingHours, analysisHours, blockElement) {
    // Excel formulas:
    // H62: =F6*(F7+F8)*46 - Total labor cost before
    const laborCostBefore = casesPerYear * (processingHours + analysisHours) * 46;

    // Calculate hours saved first (from time savings calculations)
    // old: H52: =F6*F7*I15 - Processing hours saved
    // const processingSavedHours = casesPerYear * processingHours * INVESTIGATOR_SAVINGS_PERCENTAGE;
    // H52: =F6*F7*I22 - Processing hours saved
    const processingSavedHours = casesPerYear * processingHours * EXAMINER_SAVINGS_PERCENTAGE;

    // // old: H53: =F6*F8*I22 - Analysis hours saved
    // const analysisSavedHours = casesPerYear * analysisHours * EXAMINER_SAVINGS_PERCENTAGE;
    // H53: =F6*F8*I15 - Analysis hours saved
    const analysisSavedHours = casesPerYear * analysisHours * INVESTIGATOR_SAVINGS_PERCENTAGE;

    // Calculate money saved (from Excel H34 and H39 logic)
    // H34: =H32*H33 (Investigator hours saved * $46)
    const automateValue = Math.round(processingSavedHours * INVESTIGATOR_HOURLY_COST);

    // H39: =H37*H38 (Examiner hours saved * $46)
    const reviewValue = Math.round(analysisSavedHours * EXAMINER_HOURLY_COST);

    // H43: =H39+H34 - Total value created
    const totalValueCreated = automateValue + reviewValue;

    // H64: =H62-H63 - Total labor cost after (before - savings)
    const laborCostAfter = laborCostBefore - totalValueCreated;

    // Update display elements
    const elements = {
        'labor-cost-before': formatNumber(laborCostBefore),
        'automate-value': formatNumber(automateValue),
        'review-value': formatNumber(reviewValue),
        'total-value-created': formatNumber(totalValueCreated),
        'labor-cost-after': formatNumber(laborCostAfter)
    };

    // Update all elements with calculated values
    Object.keys(elements).forEach(key => {
        const spans = blockElement.querySelectorAll(`[data-calc="${key}"]`);
        spans.forEach(span => {
            span.textContent = elements[key];
        });
    });

    // after value relative to before
    const afterWidth = (laborCostAfter / laborCostBefore).toFixed(2);

    // Update chart bar heights for value chart
    const valueChartBars = {
        'value-chart-after': afterWidth,
    };

    // Update value chart elements with height percentages
    Object.keys(valueChartBars).forEach(key => {
        const elements = blockElement.querySelectorAll(`[data-chart="${key}"]`);
        elements.forEach(element => {
            element.setAttribute('data-width-percent', valueChartBars[key]);

            if (element.tagName === 'rect' && key === 'value-chart-after') {
                element.parentNode.parentNode.style.width = `calc( ( ( 100% - 180px )  * ${afterWidth} ) + 180px)`;
            }
        });
    });
    
}

/**
 * Parse number from text with prefix/suffix support
 * Handles formats like: 49%, 18+, 4x, $1000, etc.
 */
function parseNumberFromText(text) {
    const trimmed = text.trim();
    const numberMatch = trimmed.match(/(\d+)/);

    if (!numberMatch) {
        return null;
    }

    const number = parseInt(numberMatch[1]);
    const numberIndex = numberMatch.index;
    const prefix = trimmed.substring(0, numberIndex);
    const suffix = trimmed.substring(numberIndex + numberMatch[0].length);

    return {
        number: number,
        prefix: prefix,
        suffix: suffix,
        original: trimmed
    };
}

/**
 * Animate counter from start to end with easing
 */
function animateCounter(element, start, end, prefix, suffix, duration = 1500) {
    const startTime = performance.now();

    function updateCounter(currentTime) {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Easing function for smooth animation (ease-out-quart)
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const currentValue = Math.round(start + (end - start) * easeOutQuart);

        element.textContent = prefix + currentValue + suffix;

        if (progress < 1) {
            requestAnimationFrame(updateCounter);
        }
    }

    requestAnimationFrame(updateCounter);
}

/**
 * Animate number block statistic
 */
function animateNumberBlock(element) {
    const parsed = parseNumberFromText(element.textContent);

    if (parsed && parsed.number > 1) {
        // Start animation from 1 to the target number
        animateCounter(element, 1, parsed.number, parsed.prefix, parsed.suffix);
    }
}

/**
 * Initialize number block animations
 */
function initializeNumberBlockAnimations() {
    // Create intersection observer to trigger animations when blocks come into view
    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                const statElement = entry.target.querySelector('.magnet-number-block__statistic');
                if (statElement && !statElement.dataset.animated) {
                    statElement.dataset.animated = 'true';
                    // Small delay to ensure the block is visible
                    setTimeout(() => {
                        animateNumberBlock(statElement);
                    }, 100);
                }
                // Stop observing this element after animation
                observer.unobserve(entry.target);
            }
        });
    }, {
        threshold: 0.3, // Trigger when 30% of the block is visible
        rootMargin: '0px 0px -50px 0px' // Start animation slightly before fully visible
    });

    // Observe all number blocks
    const numberBlocks = document.querySelectorAll('.magnet-number-block');
    numberBlocks.forEach(block => {
        observer.observe(block);
    });
}

/**
 * Initialize all calculators on the page
 */
function initializeAllCalculators() {
    // console.log('initializeAllCalculators');
    // Initialize main calculators
    const calculators = document.querySelectorAll('.magnet-calculator');
    calculators.forEach(calculator => {
        initializeCalculator(calculator);
    });

    // Initialize time savings blocks
    const timeSavingsBlocks = document.querySelectorAll('[data-block-type="time-savings"]');
    timeSavingsBlocks.forEach(block => {
        initializeTimeSavingsBlock(block);
    });

    // Initialize total value blocks
    const totalValueBlocks = document.querySelectorAll('[data-block-type="total-value"]');
    totalValueBlocks.forEach(block => {
        initializeTotalValueBlock(block);
    });

    // Initialize total value blocks (new)
    const timeSavingsBlocksNew = document.querySelectorAll('[data-block-type="total-time-new"]');
    timeSavingsBlocksNew.forEach(block => {
        initializeTimeSavingsBlockNew(block);
    });

    // Initialize total value blocks (new)
    const totalValueBlocksNew = document.querySelectorAll('[data-block-type="total-value-new"]');
    totalValueBlocksNew.forEach(block => {
        initializeTotalValueBlockNew(block);
    });

    // Initialize number block animations
    initializeNumberBlockAnimations();
}

function debounce(func, delay) {
    let timeout;
    return function(...args) {
        const context = this;
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(context, args), delay);
    };
}

function getCookie(key) {
    var b = document.cookie.match("(^|;)\\s*" + key + "\\s*=\\s*([^;]+)");
    return b ? b.pop() : "";
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAllCalculators);
} else {
    // DOM is already loaded
    initializeAllCalculators();
}

// Also initialize on window load as a fallback
window.addEventListener('load', initializeAllCalculators);